import { useState, useMemo, ChangeEvent, MouseEvent } from 'react';
import ProjectsReportModal from './ProjectsReportModal';
import {
  ProjectsReportProvider,
  useProjectsReport,
  ColumnDef,
} from './ProjectsReportContext';
import { FormContextProvider } from '@stormwater-software/lib-form';
import {
  Box,
  Checkbox,
  styled,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { visuallyHidden } from '@mui/utils';
import _ from 'lodash';
import { parseISO, format } from 'date-fns';
import ExcelJs from 'exceljs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

type Order = 'asc' | 'desc';

function EnhancedTableHead({
  order,
  orderBy,
  onRequestSort,
  columns,
}: {
  order: Order;
  orderBy: string;
  onRequestSort: (e: MouseEvent<unknown>, property: string) => void;
  columns: ColumnDef[];
}) {
  const createSortHandler = (property: string) => (e: MouseEvent<unknown>) =>
    onRequestSort(e, property);

  return (
    <TableHead>
      <TableRow>
        {columns
          .filter((c) => c.isEnable)
          .map((col) => (
            <TableCell
              key={col.value}
              align={col.numeric ? 'right' : 'left'}
              padding='normal'
              sortDirection={orderBy === col.value ? order : false}
              width={col.width}
              sx={{ fontWeight: 'bold' }}
            >
              <TableSortLabel
                active={orderBy === col.value}
                direction={orderBy === col.value ? order : 'asc'}
                onClick={createSortHandler(col.value)}
              >
                {col.label}
                {orderBy === col.value ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
}

const formatDate = (val: string | null) =>
  val ? format(parseISO(val), 'MM/dd/yyyy') : '';

const getCellValue = (item: any, value: string) => {
  if (value === 'startDate' || value === 'endDate') return formatDate(item[value]);
  
return item[value] ?? '';
};

const ProjectsReportInner = () => {
  const {
    data,
    setData,
    columns,
    setColumns,
    formData,
    setFormData,
    showModal,
    setShowModal,
    isModify,
    setIsModify,
    open,
    setOpen,
  } = useProjectsReport();

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('projectName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const enabledColumns = columns.filter((c) => c.isEnable);

  const onCheck = (col: ColumnDef) => {
    setColumns((prev) =>
      prev.map((c) => (c.id === col.id ? { ...c, isEnable: !c.isEnable } : c)),
    );
  };

  const handleRequestSort = (_: unknown, property: string) => {
    const isAsc = orderBy === property && order === 'asc';

    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const visibleRows = useMemo(
    () =>
      _.orderBy(data, [orderBy], [order]).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, data],
  );

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const handleDownload = () => {
    const workbook = new ExcelJs.Workbook();
    const worksheet = workbook.addWorksheet('Projects Report');

    worksheet.columns = enabledColumns.map((c) => ({
      header: c.label,
      key: c.value,
      width: 20,
    }));

    data.forEach((item: any) => {
      const row: Record<string, any> = {};

      enabledColumns.forEach((c) => {
        if (c.value === 'startDate' || c.value === 'endDate') {
          row[c.value] = item[c.value] ? parseISO(item[c.value]) : null;
        } else {
          row[c.value] = item[c.value] ?? '';
        }
      });

      worksheet.addRow(row);
    });

    worksheet.getRow(1).font = { bold: true };

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `ProjectsReport_${new Date().toJSON().slice(0, 10)}.xlsx`;
      a.click();
    });
  };

  return (
    <div className='compliance-report'>
      <div className='compliance-report__menu'>
        {data.length > 0 && (
          <div
            className='compliance-report__cog-wrapper'
            onClick={() => setOpen(!open)}
          >
            <FontAwesomeIcon icon={faCog} className='pad-left' />
          </div>
        )}

        <div
          className='compliance-report__drawer-wrapper'
          style={{ display: open ? 'block' : 'none' }}
        >
          <div className='compliance-report__drawer'>
            <p>Column Setting</p>
            <hr />
            <div
              className='compliance-report__widget-list'
              style={{ overflowY: 'auto', maxHeight: '440px' }}
            >
              {columns.map((col) =>
                col.showInOptions ? (
                  <div key={col.id} className='compliance-report__widget-list-item'>
                    <Checkbox onChange={() => onCheck(col)} checked={col.isEnable} />
                    <Typography variant='h6'>{col.label}</Typography>
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </div>
      </div>

      <FormContextProvider>
        <div
          className='compliance-report__toolbar'
          style={{ justifyContent: 'flex-end' }}
        >
          <button
            className='primary'
            style={{ marginBottom: '5px' }}
            onClick={() => {
              setIsModify(true);
              setShowModal(true);
            }}
          >
            Filter
          </button>
          <button
            className='primary'
            style={{ marginBottom: '5px' }}
            onClick={handleDownload}
            disabled={data.length === 0}
          >
            Download
          </button>
        </div>

        <TableContainer
          component={'div'}
          className='project-activity-log__table-container'
        >
          <Table aria-label='Projects Report Table'>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              columns={columns}
            />
            <TableBody>
              {visibleRows.map((item: any) => (
                <TableRow
                  key={item.projectId}
                  className='project-activity-log__body-data'
                >
                  {enabledColumns.map((col) => (
                    <StyledTableCell key={col.value}>
                      <Typography>{getCellValue(item, col.value)}</Typography>
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={enabledColumns.length} />
                </TableRow>
              )}
              {data.length === 0 && !showModal && (
                <TableRow>
                  <TableCell colSpan={enabledColumns.length} align='center'>
                    <Typography color='textSecondary'>No data found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component='div'
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '& .MuiTablePagination-actions .MuiIconButton-root': {
              backgroundColor: '#48af98',
              color: '#fff',
            },
          }}
        />

        {showModal && (
          <ProjectsReportModal
            setShowModal={setShowModal}
            setData={setData}
            formData={formData}
            setFormData={setFormData}
            isEdit={isModify}
          />
        )}
      </FormContextProvider>
    </div>
  );
};

export const ProjectsReport = () => (
  <ProjectsReportProvider>
    <ProjectsReportInner />
  </ProjectsReportProvider>
);
